import React, { useState, useEffect } from 'react';
import UserIconBlue from './icons/UserIconBlue';
import UserIconOrange from './icons/UserIconOrange';
import type { ClientInformationData, ClientInformationSectionProps, ClientStepperData } from '../../types/customer-portal';
import ClientInformationStepper from './ClientInformationStepper';
import { usePlanSelection } from '../../contexts/PlanSelectionContext';
import { sessionStateManager } from '../../utils/sessionStateManager';
import './ClientInformationSection.css';
import { useAppStore } from '@/store/useAppStore';

const ClientInformationSection: React.FC<ClientInformationSectionProps> = ({
  planType,
  planSelection,
  travelDetailsData,
  onNext
}) => {
  const { setCoverageType } = useAppStore();

  const [selectedOption, setSelectedOption] = useState<'myself' | 'someone-else' | null>(null);
  const [showStepper, setShowStepper] = useState(false);
  const [detectedPlanType, setDetectedPlanType] = useState<'Individual' | 'Family' | 'Group' | 'Multi Trip' | null>(null);
  
  // Access PlanSelectionContext for tempData
  const { getTempDataValue } = usePlanSelection();

  // ENHANCED: FLOW-AWARE PLAN TYPE DETECTION AND ROUTING WITH STEPPER STATE RESTORATION
  useEffect(() => {
    console.log('ClientInformationSection: Enhanced flow-aware plan type detection started');
    
    // STEP 1: Check for existing stepper state from page refresh
    sessionStateManager.syncStepperState();
    const stepperState = sessionStateManager.getStepperState();
    
    if (stepperState && sessionStateManager.hasValidStepperState()) {
      console.log('ClientInformationSection: ✅ RESTORING stepper state from page refresh:', stepperState);
      
      // Restore stepper state
      setSelectedOption(stepperState.selectedOption as 'myself' | 'someone-else');
      setShowStepper(stepperState.showStepper);
      setDetectedPlanType(stepperState.planType as 'Individual' | 'Family' | 'Group' | 'Multi Trip');
      
      console.log('ClientInformationSection: Stepper state restored successfully');
      return; // Exit early, stepper state takes precedence
    }
    
    // STEP 2: Normal flow detection (existing logic)
    console.log('ClientInformationSection: No stepper state found, proceeding with normal flow detection');
    
    // Method 1: Check URL path for plan type
    const currentPath = window.location.pathname;
    let detectedPlanType: string | null = null;
    
    if (currentPath.includes('/family')) {
      detectedPlanType = 'Family';
    } else if (currentPath.includes('/group')) {
      detectedPlanType = 'Group';
    } else if (currentPath.includes('/multi-trip')) {
      detectedPlanType = 'Multi Trip';
    } else if (currentPath.includes('/individual')) {
      detectedPlanType = 'Individual';
    }
    
    // Method 2: Check if this is from email verification flow
    // Check sessionStorage first, then localStorage for email verification data
    const sessionEmailVerificationComplete = sessionStorage.getItem('emailVerificationComplete');
    const localEmailVerificationComplete = localStorage.getItem('emailVerificationComplete');
    const sessionEmailVerificationTempData = sessionStorage.getItem('emailVerificationTempData');
    const localEmailVerificationTempData = localStorage.getItem('emailVerificationTempData');

    console.log('ClientInformationSection: Email verification detection:', {
      sessionEmailVerificationComplete,
      localEmailVerificationComplete,
      sessionEmailVerificationTempData: sessionEmailVerificationTempData ? 'exists' : 'null',
      localEmailVerificationTempData: localEmailVerificationTempData ? 'exists' : 'null',
      allSessionStorageKeys: Object.keys(sessionStorage),
      allLocalStorageKeys: Object.keys(localStorage)
    });
    
    const isFromEmailVerification = sessionEmailVerificationComplete === 'true' ||
                                   localEmailVerificationComplete === 'true' ||
                                   sessionEmailVerificationTempData !== null ||
                                   localEmailVerificationTempData !== null;
    
    console.log('ClientInformationSection: isFromEmailVerification =', isFromEmailVerification);
    
    // Method 3: Check props
    const propPlanType = planType;
    
    let finalPlanType: string;
    
    if (isFromEmailVerification) {
      // EMAIL VERIFICATION FLOW: Use tempData with priority
      console.log('ClientInformationSection: Email verification flow detected');
      
      const tempDataTypeName = getTempDataValue('typeName') as string;
      const tempDataResolvedPlanType = getTempDataValue('resolvedPlanType') as string;
      const tempDataPlanType = getTempDataValue('planType') as number;
      
      // Priority: tempData > URL > props
      if (tempDataResolvedPlanType) {
        finalPlanType = tempDataResolvedPlanType;
      } else if (tempDataTypeName) {
        finalPlanType = tempDataTypeName;
      } else if (tempDataPlanType) {
        switch (tempDataPlanType) {
          case 1: finalPlanType = 'Individual'; break;
          case 2: finalPlanType = 'Family'; break;
          case 3: finalPlanType = 'Group'; break;
          case 4: finalPlanType = 'Multi Trip'; break;
          default: finalPlanType = detectedPlanType || propPlanType || 'Individual'; break;
        }
      } else {
        finalPlanType = detectedPlanType || propPlanType || 'Individual';
      }
      
      console.log('ClientInformationSection: Email verification flow plan type resolution:', {
        tempDataResolvedPlanType,
        tempDataTypeName,
        tempDataPlanType,
        detectedPlanType,
        propPlanType,
        finalPlanType
      });
    } else {
      // DIRECT URL ACCESS FLOW: Use URL and props only, ignore tempData
      console.log('ClientInformationSection: Direct URL access flow detected');
      
      // Priority: URL > props > default
      finalPlanType = detectedPlanType || propPlanType || 'Individual';
      
      console.log('ClientInformationSection: Direct URL access plan type resolution:', {
        detectedPlanType,
        propPlanType,
        finalPlanType,
        ignoredTempData: 'true'
      });
    }
    
    // Store the detected plan type in state (with proper type casting)
    if (finalPlanType === 'Individual' || finalPlanType === 'Family' || finalPlanType === 'Group' || finalPlanType === 'Multi Trip') {
      setDetectedPlanType(finalPlanType);
    } else {
      setDetectedPlanType(null);
    }
    
    // ROUTING LOGIC: Different behavior based on flow
    if (isFromEmailVerification) {
      // Email verification flow: Bypass selection screen for all non-Individual plans
      if (finalPlanType === 'Family' || finalPlanType === 'Group' || finalPlanType === 'Multi Trip') {
        console.log(`ClientInformationSection: ✅ EMAIL VERIFICATION - BYPASSING selection screen for ${finalPlanType} plan`);
        setSelectedOption('myself');
        setShowStepper(true);
      } else if (finalPlanType === 'Individual') {
        console.log('ClientInformationSection: EMAIL VERIFICATION - Individual plan - showing selection screen');
      }
    } else {
      // Direct URL access flow: Only bypass for Multi Trip, show selection for others
      if (finalPlanType === 'Multi Trip') {
        console.log(`ClientInformationSection: ✅ DIRECT ACCESS - BYPASSING selection screen for ${finalPlanType} plan`);
        setSelectedOption('myself');
        setShowStepper(true);
      } else {
        console.log(`ClientInformationSection: DIRECT ACCESS - ${finalPlanType} plan - showing selection screen`);
      }
    }
  }, [planType, getTempDataValue]);

  // Legacy effect for backward compatibility (keeping the original logic as fallback)
  React.useEffect(() => {
    if (planType === 'Family' || planType === 'Group' || planType === 'Multi Trip') {
      setSelectedOption('myself');
      setShowStepper(true);
    }
  }, [planType]);

  const handleOptionSelect = (option: 'myself' | 'someone-else') => {
    setSelectedOption(option);
    
    // Show stepper immediately when option is selected
    setShowStepper(true);
    setCoverageType(option);
  };

  const handleStepperBack = () => {
    setShowStepper(false);
    setSelectedOption(null);
  };

  const handleStepperComplete = (data: ClientStepperData) => {
    console.log('Client Information Complete:', data);
    
    // Create client data object for the parent component
    const clientData: ClientInformationData = {
      coverageType: selectedOption!,
      planType: detectedPlanType || planType,
      selectedAt: new Date().toISOString()
    };
    
    if (onNext) {
      onNext(clientData);
    }
  };

  // Show stepper if an option is selected
  if (showStepper && selectedOption) {
    return (
      <ClientInformationStepper
        coverageType={selectedOption}
        planType={detectedPlanType || planType}
        planSelection={planSelection}
        travelDetailsData={travelDetailsData}
        onBack={handleStepperBack}
        onComplete={handleStepperComplete}
      />
    );
  }

  // For Family Plan, Group Plan, and Multi Trip, don't show the selection screen
  const currentPlanType = detectedPlanType || planType;
  if (currentPlanType === 'Family' || currentPlanType === 'Group' || currentPlanType === 'Multi Trip') {
    return null; // The stepper will be shown instead
  }

  return (
    <div className="client-information-container">
      <div
        className="client-information-background"
      />
      <div className="client-information-overlay">
        <div className="client-information-header">
          <h1 className="client-information-title">Client Information</h1>
          <p className="client-information-subtitle">
            Please provide details.
          </p>
        </div>
      </div>

      <div className="client-information-content">
        {/* Selection Card */}
        <div className="client-information-card">
          <h2 className="selection-question">
            Who will be covered by this travel insurance?
          </h2>

          <div className="selection-options">
            {/* Myself Option */}
            <div
              className={`selection-option ${selectedOption === 'myself' ? 'selected' : ''}`}
              onClick={() => handleOptionSelect('myself')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionSelect('myself');
                }
              }}
            >
              <div className="option-icon">
                <UserIconBlue size={48} />
              </div>
              <h3 className="option-title">Myself</h3>
              <p className="option-description">You will be the principal insured</p>
            </div>

            {/* Someone Else Option */}
            <div
              className={`selection-option ${selectedOption === 'someone-else' ? 'selected' : ''}`}
              onClick={() => handleOptionSelect('someone-else')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionSelect('someone-else');
                }
              }}
            >
              <div className="option-icon">
                <UserIconOrange size={48} />
              </div>
              <h3 className="option-title">Someone Else</h3>
              <p className="option-description">You will buy on behalf of principal insured</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInformationSection;